/**
 * Copyright 2026 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import type { EventName } from '@lit/react';
import { createComponent } from '@lit/react';
import * as React from 'react';

import { Table as SpTable } from '@spectrum-web-components/table';
import { TableBody as SpTableBody } from '@spectrum-web-components/table';
import { TableCell as SpTableCell } from '@spectrum-web-components/table';
import { TableCheckboxCell as SpTableCheckboxCell } from '@spectrum-web-components/table';
import { TableHead as SpTableHead } from '@spectrum-web-components/table';
import { TableHeadCell as SpTableHeadCell } from '@spectrum-web-components/table';
import { TableRow as SpTableRow } from '@spectrum-web-components/table';

import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-checkbox-cell.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-row.js';
import '@spectrum-web-components/table/sp-table.js';

export const Table = createComponent({
  displayName: 'Table',
  elementClass: SpTable,
  react: React,
  tagName: 'sp-table',
  events: {
    change: 'change' as EventName<Event>, // Announces a change in the `selected` property of a table row
    rangeChanged: 'rangeChanged' as EventName<Event>, // Announces a change in the range of visible cells on the table body
  },
});
export const TableBody = createComponent({
  displayName: 'TableBody',
  elementClass: SpTableBody,
  react: React,
  tagName: 'sp-table-body',
  events: {},
});
export const TableCell = createComponent({
  displayName: 'TableCell',
  elementClass: SpTableCell,
  react: React,
  tagName: 'sp-table-cell',
  events: {},
});
export const TableCheckboxCell = createComponent({
  displayName: 'TableCheckboxCell',
  elementClass: SpTableCheckboxCell,
  react: React,
  tagName: 'sp-table-checkbox-cell',
  events: {},
});
export const TableHead = createComponent({
  displayName: 'TableHead',
  elementClass: SpTableHead,
  react: React,
  tagName: 'sp-table-head',
  events: {},
});
export const TableHeadCell = createComponent({
  displayName: 'TableHeadCell',
  elementClass: SpTableHeadCell,
  react: React,
  tagName: 'sp-table-head-cell',
  events: {
    sorted: 'sorted' as EventName<CustomEvent>, // Announces that the table head has been sorted and handles the sorted event
  },
});
export const TableRow = createComponent({
  displayName: 'TableRow',
  elementClass: SpTableRow,
  react: React,
  tagName: 'sp-table-row',
  events: {
    sorted: 'sorted' as EventName<Event>, // Announces that `selected` of the table row has changed
  },
});

export type TableType = EventTarget & SpTable;
export type TableBodyType = EventTarget & SpTableBody;
export type TableCellType = EventTarget & SpTableCell;
export type TableCheckboxCellType = EventTarget & SpTableCheckboxCell;
export type TableHeadType = EventTarget & SpTableHead;
export type TableHeadCellType = EventTarget & SpTableHeadCell;
export type TableRowType = EventTarget & SpTableRow;
